import { SVGPathData } from 'svg-pathdata';

import { getHashUtil } from '../../main/utils/hash.util.ts';

import { WritingAction, WritingActionType } from '../types/sounds-writing.types.ts';
import { SvgSubPath } from '../types/svg.types.ts';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const SVG_TAG = 'svg';
const PATH_TAG = 'path';

class SvgUtil {
  private static _instance: SvgUtil;

  private constructor(private readonly hashUtil = getHashUtil()) {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): SvgUtil {
    if (!SvgUtil._instance) {
      SvgUtil._instance = new SvgUtil();
    }
    return SvgUtil._instance;
  }

  private createSvgPath(): SVGPathElement {
    const svg = document.createElementNS(SVG_NAMESPACE, SVG_TAG);
    const path = document.createElementNS(SVG_NAMESPACE, PATH_TAG);
    svg.appendChild(path);
    return path;
  }

  /**
   * Splits a path string into subpaths, each starting with 'M'
   * Also calculates the length of each subpath
   */
  getSubPaths(pathString: string): SvgSubPath[] {
    const absolutePath = new SVGPathData(pathString).toAbs().encode();
    const parentHash = this.hashUtil.hash(absolutePath);

    const subPathStrings = absolutePath.split(/(?=M)/);
    return subPathStrings.map((path) => ({
      hash: this.hashUtil.hash(parentHash + path),
      path,
      length: this.getPathLength(path),
    }));
  }

  /**
   * Gets the total length of a path string by creating a temporary SVG path
   */
  getPathLength(pathString: string): number {
    const path = this.createSvgPath();
    path.setAttribute('d', pathString);
    return path.getTotalLength();
  }

  /**
   * Converts an array of writing actions to an SVG path string
   */
  writingActionsToPath(actions: WritingAction[]): string {
    if (actions.length === 0) return '';

    return actions
      .map((action) => {
        switch (action.type) {
          case WritingActionType.MoveTo:
            return `M ${action.x} ${action.y}`;
          case WritingActionType.LineTo:
            return `L ${action.x} ${action.y}`;
        }
      })
      .join(' ');
  }
}

export const getSvgUtil = (): SvgUtil => SvgUtil.instance;
